import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthStore } from '@auth/authStore';
import { getAnomalies, getReportList } from '@entities';
import { useAsyncResource, useScopeOptions, useUrlAssetScope, buildScopedSearch } from '@hooks';
import { PageTabs } from '@components';
import DetailDialog from '@/shared/components/DetailDialog/DetailDialog';
import AnomalyDetail from '@/features/anomaly-register/components/AnomalyDetail';
import HistoryRecords from '@/features/lifecycle-history/HistoryRecords';
import { normalizeReport, attachReportLocations } from '@/entities/report/report.model';
import {
  normalizeHistoryAnomalies, reportHistoryRows, historyInScope, readHistoryComponent, matchesHistoryCode,
} from '@/entities/history/history.model';
import styles from './HistoryPage.module.css';

const tabs = [{ id: 'inspections', label: 'Inspection' }, { id: 'anomalies', label: 'Anomaly' }];

function AnomalyHistory({ companyId, siteId, assetId, componentCode }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const resource = useAsyncResource(async () => normalizeHistoryAnomalies(
    await getAnomalies({ companyId }), companyId
  ), [companyId], { initialData: null });
  const rows = useMemo(() => (resource.data?.rows || []).filter(row =>
    historyInScope(row, siteId, assetId) && matchesHistoryCode(row, componentCode)),
    [resource.data, siteId, assetId, componentCode]);
  const selectedId = params.get('history.selected');
  const selected = rows.find(row => row.id === selectedId);
  function select(row = null) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (row) next.set('history.selected', row.id); else next.delete('history.selected');
      return next;
    });
  }
  if (resource.isLoading) return <p role="status" className={styles.feedback}>Loading anomaly records…</p>;
  if (resource.error) return <div className={styles.notice}><p role="alert">{resource.error}</p>
    <button type="button" className={styles.button} onClick={() => resource.reload()}>Retry anomaly history</button></div>;
  return <div className={styles.content}>
    <header className={styles.subheading}><h2>Anomaly record chronology</h2>
      <p>Ordered by recorded date raised. Status and severity describe the current record, not its historical state.</p></header>
    <p className={styles.notice}>Closure, reopening, severity changes and repeated observations are not available as dated events.
      Preceded-by/succeeded-by values remain reference metadata; no event chain is inferred.</p>
    {resource.data?.excluded > 0 && <p className={styles.note}>{resource.data.excluded} record(s) with another company ID were excluded.</p>}
    <p className={styles.note}>Rows without site/asset IDs remain visible at fleet scope; they are not assigned to a location by guesswork.
      A missing date is not a new event and does not use today's date.</p>
    {selectedId && !selected && <div className={styles.notice}><p>The selected anomaly is not available in this company/location/component scope.</p>
      <button type="button" className={styles.link} onClick={() => select()}>Dismiss selection</button></div>}
    <HistoryRecords rows={rows} kind="anomaly" onOpen={select} selectedId={selectedId} />
    {selected && <DetailDialog key={selected.id} eyebrow="ANOMALY RECORD" title={selected.reference}
      subtitle={(selected.day || 'No usable date raised') + ' · Current values shown'} onClose={() => select()}>
      <div className={styles.notice}>
        <button type="button" className={styles.button} onClick={() => {
          const hasLocation = Boolean(selected.siteId && selected.assetId);
          const search = buildScopedSearch('', {
            siteId: hasLocation ? selected.siteId : null,
            assetId: hasLocation ? selected.assetId : null,
          }, { anomalyView: 'register', 'anomalies.selected': selected.id });
          navigate({ pathname: '/anomaly-suite', search: '?' + search });
        }}>Open in anomaly register</button>
        {(!selected.siteId || !selected.assetId) && <p>
          This record has incomplete location metadata. It will open at fleet scope.
        </p>}
      </div>
      <AnomalyDetail content={selected.raw} showHistoryLink={false} />
    </DetailDialog>}
  </div>;
}

function InspectionHistory({ companyId, siteId, assetId }) {
  const navigate = useNavigate();
  const locations = useScopeOptions();
  const resource = useAsyncResource(async () => {
    const value = await getReportList(companyId);
    if (!Array.isArray(value)) throw new Error('The report API must return a list.');
    return value.map(normalizeReport).filter(row => row.companyId === String(companyId).trim());
  }, [companyId], { initialData: [] });
  const model = useMemo(() => {
    try {
      const all = reportHistoryRows(attachReportLocations(resource.data, locations.assets, locations.sites));
      return {
        rows: all.filter(row => historyInScope(row, siteId, assetId)),
        unresolved: all.filter(row => (!assetId || row.assetId === assetId) && !row.siteId).length,
        error: null,
      };
    } catch (error) { return { rows: [], unresolved: 0, error: error.message }; }
  }, [resource.data, locations.assets, locations.sites, siteId, assetId]);
  if (resource.isLoading || locations.isLoading) return <p role="status" className={styles.feedback}>Loading report chronology…</p>;
  if (resource.error || model.error) return <div className={styles.notice}><p role="alert">{resource.error || model.error}</p>
    <button type="button" className={styles.button} onClick={() => resource.reload()}>Retry reports</button></div>;
  if (locations.error && siteId) return <div className={styles.notice}><p role="alert">Site scope cannot be resolved: {locations.error}</p>
    <button type="button" className={styles.button} onClick={() => locations.reload()}>Retry locations</button></div>;
  return <div className={styles.content}>
    <header className={styles.subheading}><h2>Report chronology</h2><p>Existing report records, ordered by their recorded report date.</p></header>
    <p className={styles.notice}>Inspection events are not connected. A report date is not proof of inspection completion,
      component coverage or report revision history. Local MFS drafts are not historical inspection events.</p>
    {locations.error && <div className={styles.notice}><p>Location names and report site resolution are unavailable.</p>
      <button type="button" className={styles.link} onClick={() => locations.reload()}>Retry locations</button></div>}
    {model.unresolved > 0 && <p className={styles.notice}>{model.unresolved} report(s) could not be assigned to one site.
      {siteId ? ' They are excluded from this site scope.' : ' They remain in the fleet list.'}</p>}
    <HistoryRecords rows={model.rows} kind="report" onOpen={row => {
      const search = buildScopedSearch('', {
        siteId: row.siteId || null, assetId: row.siteId ? row.assetId : null,
      }, { 'reports.selected': row.id });
      navigate({ pathname: '/report-browser', search: '?' + search });
    }} />
  </div>;
}

function CompanyHistory({ companyId }) {
  const { siteId, assetId } = useUrlAssetScope();
  const [params, setParams] = useSearchParams();
  const view = params.get('historyView') === 'inspections' ? 'inspections' : 'anomalies';
  const component = readHistoryComponent(params, siteId, assetId, companyId);
  const scopeKey = JSON.stringify([companyId, siteId, assetId, component.code]);
  function clearComponent() {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.delete('history.component'); next.delete('history.componentScope'); next.delete('history.selected');
      return next;
    });
  }
  return <section className={styles.page}>
    <header className={styles.heading}><span>LIFECYCLE HISTORY</span><h1>Records through time</h1>
      <p>Review dated records and their evidence. A complete change-event audit trail is not available yet.</p></header>
    <div className={styles.context}><strong>{assetId ? 'Asset scope' : siteId ? 'Site scope' : 'Fleet scope'}</strong>
      <span>{siteId || 'All sites'}{assetId ? ' / ' + assetId : ''}</span>
      {component.code && <><span>RDS code: {component.code}</span><button type="button" className={styles.button} onClick={clearComponent}>Clear component context</button></>}
    </div>
    <PageTabs id="history" tabs={tabs} current={view} label="History views" onChange={nextView => {
      setParams(previous => {
        const next = new URLSearchParams(previous); next.set('historyView', nextView); next.delete('history.selected'); return next;
      });
    }} />
    {tabs.map(tab => <section key={tab.id} id={'history-panel-' + tab.id} role="tabpanel"
      aria-labelledby={'history-tab-' + tab.id} hidden={view !== tab.id} tabIndex={0} className={styles.panel}>
      {view === tab.id && (!component.valid ? <p className={styles.notice}>The component context belongs to a different site/asset.
        Clear component context to use the current scope.</p> :
        view === 'inspections' && component.code ? <div className={styles.notice}>
          <h2>Component inspection events are not connected</h2>
          <p>The report list does not identify inspected components. Asset reports cannot be presented as this component's inspection history.</p>
          <button type="button" className={styles.button} onClick={clearComponent}>Show asset report chronology</button>
        </div> : view === 'anomalies' ?
          <AnomalyHistory key={scopeKey} companyId={companyId} siteId={siteId} assetId={assetId} componentCode={component.code} /> :
          <InspectionHistory key={scopeKey} companyId={companyId} siteId={siteId} assetId={assetId} />)}
    </section>)}
  </section>;
}

export default function HistoryPage() {
  const companyId = useAuthStore(state => state.companyID);
  return companyId ? <CompanyHistory key={String(companyId)} companyId={companyId} /> :
    <section className={styles.page}><p>Select a company to view its records.</p></section>;
}